
import {
    computed,
    defineComponent,
    Emitter,
    inject,
    onBeforeMount,
    onBeforeUnmount,
    onMounted,
    PropType,
    ref,
    SocketMessage,
    TableData,
    UserInfo,
    watch
} from 'vue'
import { useI18n } from 'vue-i18n'
import Dropdown2 from '@/components/drop-down-2.vue'
import CountdownTimer from '@/components/timer-count-down.vue'
import { useListItem } from '@/composables/useListitem'
import { RoadMapData } from './roadmap/datamodel/RoadMapData'
import { parseRoadMap } from './roadmap/utils'
import DPrediction1 from '@/components/d-prediction-1.vue'
import Roadmap from '@/components/roadmap/index.vue'
import { Desk } from '@/types/Desk'
import { RoadMapSummaryData } from './roadmap/datamodel/types'
import BetGroup from '@/components/ctrl-bet-box-group-multi.vue'
import { EventBuss, RESULT, SOCKET_MESSAGE } from '@/types/global'
import { useStore } from '@/store'
import { useRouter } from 'vue-router'
import { ROUTES } from '@/router'
import { GameResult } from '@/types/GameResult'
// loads global v_*/h_* card sprite styles used by multi-card-result
import '@/components/d-card-result.vue'

export default defineComponent({
    emits: ['select-table', 'remove', 'stop-betting'],
    props: {
        position: {
            type: Number as PropType<number>,
            required: true
        },
        desk: {
            type: Object as PropType<TableData>,
            required: false
        }
    },
    components: {
        'drop-down-2': Dropdown2,
        'count-down-timer': CountdownTimer,
        'd-prediction-1': DPrediction1,
        'roadmap': Roadmap,
        'bet-group': BetGroup
    },
    setup(props, { emit }) {
        const emitter = inject('emitter') as Emitter
        const router = useRouter()
        const store = useStore()
        const { t } = useI18n()
        let countDownInterval: boolean | number = false
        const currentCount = ref<number>(-1)
        const roadMapData = ref<RoadMapData>(parseRoadMap([]))
        const nextRedRoadMapSummaryData = ref<RoadMapSummaryData>({})
        const nextBlueRoadMapSummaryData = ref<RoadMapSummaryData>({})
        const roadmapview12 = ref<typeof Roadmap | undefined>(undefined)
        // eslint-disable-next-line no-undef
        const nodePlayer = ref<NodePlayer | undefined>(undefined)
        let videoPlayerCollection: Array<any> = new Array<any>()

        const videoRef1 = ref<HTMLElement | undefined>()
        const videoRef2 = ref<HTMLElement | undefined>()

        const hasTempBet = ref(false)

        const refBetGroup = ref<any>()

        const videoIsLoading = ref(true)
        const mapKey = ref(new Date().getTime())
        const showMessageBox = ref(false)
        const isFreeBaccarat = ref(false)
        const allowToggleGameMode = ref(false)
        const enableFreeBaccarat = ref(false)
        const showCardDisplay = ref(false)
        const cardSnap = ref<{
            blueTotal: number
            redTotal: number
            whoWin: number
            label: string
            blueCard: string[]
            redCard: string[]
        } | null>(null)
        let showCardTimeout: number | boolean = false

        onBeforeMount(() => {
            enableFreeBaccarat.value = String(process.env.ENABLE_FREE_BACCARAT).toLowerCase() === 'true'
        })

        onMounted(() => {
            emitter.on(EventBuss.MULTI_CANCEL_GAME, handleCancelGame)
            emitter.on(EventBuss.MULTI_END_GAME, handleEndGame)
            emitter.on(EventBuss.SOCKET_MESSAGE, handleSocketMessage)
        })

        onBeforeUnmount(() => {
            emitter.off(EventBuss.MULTI_CANCEL_GAME, handleCancelGame)
            emitter.off(EventBuss.MULTI_END_GAME, handleEndGame)
            emitter.off(EventBuss.SOCKET_MESSAGE, handleSocketMessage)
            stopCountDown()
            disposeVideoPlayer()
            if (typeof showCardTimeout === 'number') {
                clearTimeout(showCardTimeout)
                showCardTimeout = false
            }
        })

        const clearCardResultDisplay = () => {
            showCardDisplay.value = false
            cardSnap.value = null
            refBetGroup.value?.hideBetViewResult()
            if (typeof showCardTimeout === 'number') {
                clearTimeout(showCardTimeout)
                showCardTimeout = false
            }
        }

        const buildCardLabel = (whoWin: number, gType: number) => {
            const win = Number(whoWin)
            if (win === RESULT.RED && gType === 1) return `${t('roadmap.banker')}赢`
            if (win === RESULT.BLUE && gType === 1) return `${t('roadmap.player')}赢`
            if (win === RESULT.GREEN || win === RESULT.DRAGON_TIGER_TIE)
                return `${t('roadmap.tie')}赢`
            if (win === RESULT.DRAGON) return `${t('roadmap.dragon')}赢`
            if (win === RESULT.TIGER) return `${t('roadmap.tiger')}赢`
            return ''
        }

        const showCardAndBetResult = (desk?: TableData) => {
            const source = desk || props.desk
            if (!source?.expand) return

            const expand = source.expand
            const hasCards = !!(expand.playerOne || expand.bankerOne)
            const whoWinNum = Number(expand.whoWin ?? -1)
            if (!hasCards && !(whoWinNum > 0)) return

            if (typeof showCardTimeout === 'number') {
                clearTimeout(showCardTimeout)
                showCardTimeout = false
            }

            let blueTotal = Number(expand.playerCount ?? 0)
            let redTotal = Number(expand.bankerCount ?? 0)
            let whoWin = whoWinNum
            const blueCard: string[] = []
            const redCard: string[] = []

            if (expand.playerOne) blueCard.push(String(expand.playerOne))
            if (expand.playerTwo) blueCard.push(String(expand.playerTwo))
            if (expand.playerThree) blueCard.push(String(expand.playerThree))
            if (expand.bankerOne) redCard.push(String(expand.bankerOne))
            if (expand.bankerTwo) redCard.push(String(expand.bankerTwo))
            if (expand.bankerThree) redCard.push(String(expand.bankerThree))

            // prefer computed GameResult totals when cards exist
            try {
                const deskResult = new GameResult(source)
                if (deskResult.hasCardToDisplay || blueCard.length || redCard.length) {
                    blueTotal = deskResult.blueTotal
                    redTotal = deskResult.redTotal
                    whoWin = deskResult.whoWin
                    refBetGroup.value?.showBetViewResult(deskResult)
                }
            } catch {
                // keep expand values
            }

            const gType = source.gameTypeNo === 'G002' ? 1 : 2
            cardSnap.value = {
                blueTotal,
                redTotal,
                whoWin,
                label: buildCardLabel(whoWin, gType),
                blueCard,
                redCard
            }
            showCardDisplay.value = true

            showCardTimeout = window.setTimeout(() => {
                clearCardResultDisplay()
            }, 6000)
        }

        const isSameDesk = (id: unknown) => {
            if (!props.desk) return false
            return Number(props.desk.id) === Number(id)
        }

        const handleCancelGame = (_deskId: number) => {
            if (isSameDesk(_deskId)) {
                clearCardResultDisplay()
                if (refBetGroup.value) {
                    refBetGroup.value.endGame()
                }
            }
        }

        const handleEndGame = (payload: number | TableData) => {
            const endDesk =
                typeof payload === 'object' && payload ? payload : undefined
            const deskId =
                typeof payload === 'object' && payload ? payload.id : payload

            if (isSameDesk(deskId)) {
                showCardAndBetResult(endDesk || props.desk)
                setTimeout(() => {
                    refBetGroup.value?.endGame()
                }, 250)
            }
        }

        // const refreshRoamapViewKey = () => {
        //     mapKey.value = new Date().getTime()
        // }

        const startCountDown = () => {
            if (typeof countDownInterval !== 'number') {
                currentCount.value = props.desk!.expand.daoJiShiBetTime
                allowToggleGameMode.value = true

                countDownInterval = setInterval(() => {
                    if (currentCount.value < 0) {
                        stopCountDown()
                    } else {
                        currentCount.value -= 1
                    }
                }, 1000)
            }
        }

        const stopCountDown = (): void => {
            if (typeof countDownInterval === 'number') {
                clearInterval(countDownInterval)
                countDownInterval = false
                currentCount.value = -1
                if (props.desk) {
                    emit('stop-betting', props.desk.id)
                    if (refBetGroup.value) {
                        refBetGroup.value.handleNotifyStatus(props.desk.status)
                    }
                }

                if (refBetGroup.value) {
                    refBetGroup.value.resetTempBet()
                }
            }
        }

        const predict = (preType: number) => {
            if (roadmapview12.value) {
                roadmapview12.value.Predict(preType)
            }
        }

        const onRoadMapDataParsed = (
            a: RoadMapData,
            b: RoadMapData,
            c: RoadMapData
        ) => {
            roadMapData.value = a
            nextRedRoadMapSummaryData.value = { data: b }
            nextBlueRoadMapSummaryData.value = { data: c }
        }

        const onPlaceTempBetOrClear = (_hasTempAndConfirmBet: boolean) => {
            hasTempBet.value = _hasTempAndConfirmBet
            // console.log('hsdfhe' + _hasTempAndConfirmBet)
        }

        const confirmTempBet = () => {
            if (refBetGroup.value) refBetGroup.value.confirmTempBet()
        }

        const cancelTempbet = () => {
            if (refBetGroup.value) refBetGroup.value.resetTempBet()
        }

        const enterTable = () => {
            if (props.desk)
                router.push({
                    name: ROUTES.GAME,
                    query: { desk: props.desk.id }
                })
        }

        const tooggleGameMode = (isFree: boolean) => {
            isFreeBaccarat.value = isFree
        }

        const onBetModeChange = (_isFreeBaccarat: boolean) => {
            isFreeBaccarat.value = _isFreeBaccarat
            allowToggleGameMode.value = false
        }

        const isBaccaratTable = computed(() => {
            return props.desk?.gameTypeNo.toUpperCase() === 'G002'
        })

        const playMode = computed((): number => store.getters['playMode'])

        watch(playMode, () => {
            if (isBaccaratTable.value && refBetGroup.value) {
                refBetGroup.value.resetTempBet()
            }
        })

        // region Video Player Helpers
        const initVideoPlayer = () => {
            videoIsLoading.value = true
            disposeVideoPlayer()

            // console.log

            if (!videoRef1.value || !videoRef2.value) return

            // const videoPaths = [
            //     process.env.VIDEO1,
            //     process.env.VIDEO2,
            //     process.env.VIDEO3
            // ]
            let isVideoConnected = false

            // const videoUrl = props.desk!.cmccLine || ''
            const videoPaths = [props.desk?.cmccLine || '']

            videoPaths.forEach((vPath, _vId) => {
                // const _videoUrl =
                //     vPath +
                //     '/' +
                //     videoUrl.replace(/(http:|)(^|\/\/)(.*?\/)/g, '')

                const _videoUrl = vPath

                // eslint-disable-next-line no-undef
                let _player: any = new NodePlayer()

                _player.setView(videoRef1.value!.id)
                _player.setVolume(0)
                _player.setBufferTime(1000)
                _player.on('start', () => {
                    if (!isVideoConnected) {
                        isVideoConnected = true

                        nodePlayer.value = _player

                        nodePlayer.value!.clearView()
                        nodePlayer.value!.setView(videoRef2.value!.id)
                        nodePlayer.value!.on(
                            'buffer',
                            (evt: 'empty' | 'buffering' | 'full') => {
                                if (evt === 'empty' || evt === 'buffering') {
                                    videoIsLoading.value = true
                                } else if (evt === 'full') {
                                    videoIsLoading.value = false
                                }
                            }
                        )
                        nodePlayer.value!.on('error', () => {
                            videoIsLoading.value = true
                            setTimeout(() => {
                                nodePlayer.value!.clearView()
                                nodePlayer.value!.stop()
                                initVideoPlayer()
                            }, 1000)
                        })
                    } else {
                        _player.stop()
                        _player.clearView()
                        _player = undefined
                    }
                })

                _player['vId'] = String(_vId)
                videoPlayerCollection.push(_player)
                _player.start(_videoUrl)
            })
        }

        const disposeVideoPlayer = () => {
            if (nodePlayer.value) {
                nodePlayer.value.stop()
                nodePlayer.value.clearView()
                nodePlayer.value = undefined
                videoIsLoading.value = true
            }

            // eslint-disable-next-line no-undef
            videoPlayerCollection.forEach((v: NodePlayer) => {
                v.clearView()
                v.stop()
            })

            videoPlayerCollection = []
        }

        const kickOutFromTable = () => {
            showMessageBox.value = false

            emit('remove', props.position)
        }

        const handleSocketMessage = (_message: SocketMessage | any) => {
            if (!_message) return
            const { type } = _message
            const _type = String(type || '').toUpperCase()

            if (_type === SOCKET_MESSAGE.TABLE_VISIT_STATUS) {
                if (
                    props.desk &&
                    props.desk.reserverd === 1 &&
                    userInfo.value
                ) {
                    if (props.desk.id === _message.tableId) {
                        const visitCodes: string[] =
                            _message.visitCode.split(',')
                        if (
                            _message.visitCode === '' ||
                            visitCodes.indexOf(userInfo.value.code) === -1
                        ) {
                            showMessageBox.value = true
                        }
                    }
                }
                return
            }

            const sDesk = _message.gameTable as TableData | undefined
            if (!sDesk || !isSameDesk(sDesk.id)) return

            if (_type === SOCKET_MESSAGE.END_GAME) {
                showCardAndBetResult(sDesk)
                setTimeout(() => {
                    refBetGroup.value?.endGame()
                }, 250)
            } else if (_type === SOCKET_MESSAGE.NEW_GAME) {
                clearCardResultDisplay()
                if (refBetGroup.value) {
                    refBetGroup.value.handleNotifyStatus(1)
                }
            }
        }

        // endregion

        const tableName = computed(() => {
            if (props.desk) {
                const { tableNo, commandType } = props.desk

                // let cmd = ''
                // if (commandType === 0) cmd = '通用台'
                // else if (commandType === 1) cmd = '电投台'
                // else if (commandType === 2) cmd = '指令台'

                // if (props.desk.gameTypeNo.toUpperCase() === 'G002')
                //     return `${t('baccarattable')}${tableNo}`
                // // return `${t('baccarattable')}${tableNo}(${cmd})`
                // else return `${t('dragontigertable')}${tableNo}`
                // //return `${t('dragontigertable')}${tableNo}(${cmd})`

                let cmd = ''
                if (commandType === 0) cmd = t('loby.universal')
                else if (commandType === 1) t('loby.phonemode')
                else if (commandType === 2) t('loby.commandmode')
                const prefix =
                    props.desk.fastType === 1 ? t('loby.fast') : t('loby.classic')

                if (props.desk.gameTypeNo.toUpperCase() === 'G002')
                    return `${prefix}${t('loby.baccarattable')}${tableNo}`
                else return `${t('loby.dragontigertable')}${tableNo}`
            }

            return ''
        })

        const gameNo = computed(() => {
            if (props.desk) {
                return props.desk.expand.gameNo
            }

            return ''
        })

        const showAndJuNo = computed(() => {
            if (props.desk) {
                const { shoeNo, juNo } = props.desk.expand
                return `${shoeNo}靴${juNo}局`
            }

            return '0靴0局'
        })

        const redLimit = computed(() => {
            if (props.desk) {
                return `${props.desk.downLimit}-${props.desk.upLimit}`
            }
            return '0-0'
        })

        const redCount = computed(() => {
            if (roadMapData.value) return roadMapData.value.redCount
            return 0
        })

        const blueCount = computed(() => {
            if (roadMapData.value) return roadMapData.value.blueCount
            return 0
        })

        const greenCount = computed(() => {
            if (roadMapData.value) return roadMapData.value.tieCount
            return 0
        })

        const redPairCount = computed(() => {
            if (roadMapData.value) return roadMapData.value.redPairCount
            return 0
        })

        const bluePairCount = computed(() => {
            if (roadMapData.value) return roadMapData.value.bluePairCount
            return 0
        })

        const gameType = computed(() => {
            if (props.desk) {
                return props.desk.gameTypeNo === 'G002' ? 1 : 2
            }

            return 1
        })


        const limitData = computed((): string[] => {
            if (props.desk) {
                let list = []
                const {
                    downLimit,
                    upLimit,
                    pairDownLimit,
                    pairUpLimit,
                    tieDownLimit,
                    tieUpLimit,
                    superSixDownLimit,
                    superSixUpLimit,
                    bearEightDownLimit,
                    bearEightUpLimit,
                    smallTigerDownLimit,
                    smallTigerUpLimit,
                    bigTigerDownLimit,
                    bigTigerUpLimit,
                    dragonSevenDownLimit,
                    dragonSevenUpLimit,
                } = props.desk

                if (gameType.value === 1) {
                    list.push(`庄闲:${downLimit}-${upLimit}`)
                    list.push(`和:${tieDownLimit}-${tieUpLimit}`)
                    list.push(`对子:${pairDownLimit}-${pairUpLimit}`)
                    list.push(`SUPER6:${superSixDownLimit}-${superSixUpLimit}`)
                    list.push(`熊8:${bearEightDownLimit}-${bearEightUpLimit}`)
                    list.push(`小老虎:${smallTigerDownLimit}-${smallTigerUpLimit}`)
                    list.push(`大老虎:${bigTigerDownLimit}-${bigTigerUpLimit}`)
                    list.push(`龙7:${dragonSevenDownLimit}-${dragonSevenUpLimit}`)
                    return list
                } else {
                    list.push(`龙虎:${downLimit}-${upLimit}`)
                    list.push(`和:${tieDownLimit}-${tieUpLimit}`)
                    return list
                }
            }
            return []
        })

        const mapData = computed(() => {
            if (props.desk) return props.desk.expand.resultList
            return []
        })

        const userInfo = computed((): UserInfo => store.getters['userInfo'])

        const rebateType = computed((): number => store.getters['rebateType'])

        watch(
            () => props.desk,
            (_value, _oldvalue) => {
                // console.log('APPP')
                if (_value) {
                    if (_value.expand.daoJiShiBetTime > 0) {
                        startCountDown()
                    } else {
                        stopCountDown()
                    }

                    if (!_oldvalue) {
                        setTimeout(() => {
                            initVideoPlayer()
                        }, 1500)
                    } else {
                        if (_value.tableNo !== _oldvalue?.tableNo) {
                            setTimeout(() => {
                                initVideoPlayer()
                            }, 1500)
                        }

                        if (_value.status !== _oldvalue.status) {
                            // console.log('status change')
                            if (refBetGroup.value) {
                                refBetGroup.value.handleNotifyStatus(
                                    _value.status
                                )
                            }

                            // new round — hide card/bet result
                            if (_value.status === 1) {
                                clearCardResultDisplay()
                            }
                        }
                    }
                } else {
                    disposeVideoPlayer()
                    stopCountDown()
                    clearCardResultDisplay()
                    // console.log('dispose video and timer')
                }
            },
            { immediate: true }
        )

        // also catch end-game via desk whoWin / resultList updates from Loby
        watch(
            () => [
                props.desk?.id,
                props.desk?.expand?.whoWin,
                props.desk?.expand?.playerOne,
                props.desk?.expand?.bankerOne,
                props.desk?.expand?.resultList?.length
            ],
            (curr, prev) => {
                if (!props.desk?.expand || !prev) return
                const whoWin = Number(curr?.[1] ?? -1)
                const oldWhoWin = Number(prev?.[1] ?? -1)
                const hasNewCards =
                    (!!curr?.[2] && curr?.[2] !== prev?.[2]) ||
                    (!!curr?.[3] && curr?.[3] !== prev?.[3])
                const resultGrew =
                    typeof curr?.[4] === 'number' &&
                    typeof prev?.[4] === 'number' &&
                    (curr[4] as number) > (prev[4] as number)

                if (
                    whoWin > 0 &&
                    (whoWin !== oldWhoWin || hasNewCards || resultGrew)
                ) {
                    showCardAndBetResult(props.desk)
                }
            }
        )

        watch(
            () => props.desk,
            (_value) => {
                if (_value && _value.expand.daoJiShiBetTime > 0) {
                    allowToggleGameMode.value = true
                }
            },
            { deep: true }
        )

        watch(
            () => props.desk,
            (_value) => {
                // console.log('chage')
                if (_value) {
                    if (props.desk && _value.reserverd === 1) {
                        const visitCodes = _value.visitCode?.split(',') || []
                        if (visitCodes.indexOf(userInfo.value.code) === -1) {
                            showMessageBox.value = true
                        } else {
                            showMessageBox.value = false
                        }
                    } else {
                        showMessageBox.value = false
                    }
                } else {
                    showMessageBox.value = false
                }
            },
            { deep: true, immediate: true }
        )

        return {
            rebateType,
            tableName,
            showAndJuNo,
            redLimit,
            gameNo,
            redCount,
            blueCount,
            greenCount,
            redPairCount,
            bluePairCount,
            gameType,
            mapData,
            nextRedRoadMapSummaryData,
            nextBlueRoadMapSummaryData,
            roadmapview12,
            currentCount,
            videoRef1,
            videoRef2,
            mapKey,
            refBetGroup,
            hasTempBet,
            videoIsLoading,
            limitData,
            showMessageBox,
            kickOutFromTable,
            confirmTempBet,
            cancelTempbet,
            predict,
            onRoadMapDataParsed,
            onPlaceTempBetOrClear,
            enterTable,
            isFreeBaccarat,
            allowToggleGameMode,
            isBaccaratTable,
            enableFreeBaccarat,
            tooggleGameMode,
            onBetModeChange,
            showCardDisplay,
            cardSnap,
            t
        }
    }
})
